import { TableData } from '@anytag/shared-types';
import { Button, SelectionBar, TableV2, BaseModal } from '@anytag/shared-atoms';
import {
  useToggleState,
  useQueryHelper,
  useSelectItem,
  useSelectItemIds,
  useDeepCompareEffect
} from '@anytag/shared-hooks';
import {
  namedOperations,
  MarketplaceAffiliatePayoutPeriodInput,
  MarketplaceAffiliateCommissionInfluencer,
  useApproveMarketplaceAffiliatePayoutMutation
} from '@anytag/graphql';
import { useMarketplaceCommissionsFilter } from '../MarketplaceCommissionsFilter';
import { useMarketplaceCommissionsInfluencersTable } from './hooks';

export interface MarketplaceCommissionsListProps extends TableData<MarketplaceAffiliateCommissionInfluencer> {
  marketplaceId: number;
}
export const MarketplaceCommissionsList = ({ loading, data = [], marketplaceId }: MarketplaceCommissionsListProps) => {
  const confirmModal = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { filter } = useMarketplaceCommissionsFilter();
  const { selectedItem, resetSelectedItem, setSelectedItem } = useSelectItem();
  const { selectItem, selectedItemIds, hasAllItemsSelected, setSelectedItemIds, toggleSelectAllItems } =
    useSelectItemIds({ allItemIds: data.map(({ influencer }) => influencer.id) });
  const { columns } = useMarketplaceCommissionsInfluencersTable({
    data,
    selectItem,
    selectedItemIds,
    hasAllItemsSelected,
    toggleSelectAllItems,
    onApprove: (influencer) => {
      setSelectedItem(influencer);
      confirmModal.open();
    }
  });
  const { callApproveMarketplaceAffiliatePayout, loading: approving } = useApproveMarketplaceAffiliatePayoutMutation({
    refetchQueries: [namedOperations.Query.MarketplaceAffiliateCommissionInfluencers]
  });

  useDeepCompareEffect(() => {
    resetSelectedItems();
  }, [data]);

  const resetSelectedItems = () => {
    resetSelectedItem();
    setSelectedItemIds([]);
  };

  const handleCloseModal = () => {
    resetSelectedItems();
    confirmModal.close();
  };

  const handleApprove = async () => {
    try {
      const allSelectedItemIds = selectedItem ? [selectedItem.id] : selectedItemIds;
      await callApproveMarketplaceAffiliatePayout({
        variables: {
          input: {
            marketplaceId,
            coveragePeriods: data.reduce<MarketplaceAffiliatePayoutPeriodInput[]>(
              (acc, curr) =>
                allSelectedItemIds.includes(curr.influencer.id)
                  ? [...acc, { fixedDate: curr.fixedDate, postId: curr.postId }]
                  : acc,
              []
            )
          }
        }
      });

      handleCloseModal();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <div className="relative">
        {selectedItemIds.length > 0 ? (
          <SelectionBar
            disabled={approving}
            selectAllProps={{ onClick: toggleSelectAllItems }}
            checkboxProps={{
              disabled: approving,
              onClick: toggleSelectAllItems,
              indeterminate: !hasAllItemsSelected,
              label: t('Selected', { count: selectedItemIds.length })
            }}
          >
            <Button
              variant="solid"
              title={t('Button.Approve Commission')}
              onClick={confirmModal.open}
              className="ml-6 px-4"
            />
          </SelectionBar>
        ) : null}

        <TableV2
          loading={loading}
          data={data}
          columns={columns}
          rowHeight="64px"
          empty={{ description: filter.keyword ? undefined : t('Nobody joined this campaign yet') }}
          noTopBorderRadius
          stickyHeader
        />
      </div>

      <BaseModal
        open={confirmModal.status}
        onClose={handleCloseModal}
        title={t('Are you sure to approve the payout?')}
        description={
          <>
            {t('Creators will be able to withdraw commission fee after you approved')}
            <br />
            {t('This operation cannot be canceled once you click “approve”')}
          </>
        }
        footer={{
          cancelButton: { onClick: handleCloseModal },
          submitButton: { loading: approving, onClick: handleApprove, title: t('Button.Approve') }
        }}
      />
    </>
  );
};
